import { useAuth } from "@/data/context/authContext";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../UI/Buttons/Button";
import authService from "@/services/authService";
import WarningIcon from "@/assets/icons/warning.svg?react";
import CheckmarkIcon from "@/assets/icons/checkmark.svg?react";
import ClockIcon from "@/assets/icons/time.svg?react";
import { cn } from "@/lib/utils";
import { User } from "@/data/interfaces/User";
import ROUTES from "@/data/Routes";
import { REGISTRATION_STEPS } from "@/data/interfaces/RegistrationSteps";

type StepStatus = "completed" | "pending" | "action-required";

interface JourneyStep {
  id: string;
  label: string;
  status: StepStatus;
  requiredStep?: REGISTRATION_STEPS | "always";
  isCompleted?: boolean;
  action?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
    successMsg?: string;
  };
  onClick?: () => void;
}

const Icons: Record<
  StepStatus,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  completed: CheckmarkIcon,
  pending: ClockIcon,
  "action-required": WarningIcon,
};

const getIcon = (status: JourneyStep["status"]) => {
  const Icon = Icons[status];

  return (
    <div
      className={cn(
        "bg-stroke flex size-13 shrink-0 items-center justify-center rounded-full",
        status === "completed" && "bg-green",
        status === "action-required" && "bg-warning",
      )}
    >
      <Icon className="size-8" />
    </div>
  );
};

const UserJourneyPanelSteps = ({ user }: { user: User }) => {
  const navigate = useNavigate();
  const [resendingEmail, setResendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const isEmailVerified = !!user.email_verified_at;
  const hasCompletedQuestionnaire = user.type_form_submitted;

  const handleResendEmail = useCallback(async () => {
    setResendingEmail(true);
    setEmailSent(false);
    try {
      await authService.resendEmailVerification();
      setEmailSent(true);
    } catch (error) {
      console.error("Failed to resend email verification", error);
    } finally {
      setResendingEmail(false);
    }
  }, []);

  const { requiredSteps, stepCompletion } = useMemo(() => {
    const steps = new Set<REGISTRATION_STEPS>();
    const completion = new Map<REGISTRATION_STEPS, boolean>();

    const hasVisitRegistrations = (user.visit_registrations?.length ?? 0) > 0;
    if (hasVisitRegistrations) {
      steps.add(REGISTRATION_STEPS.QUESTIONAIRE);
      completion.set(
        REGISTRATION_STEPS.QUESTIONAIRE,
        hasCompletedQuestionnaire,
      );
    }

    user.visit_registrations?.forEach((vr) => {
      const productSteps =
        vr.order_item?.product?.registration_step?.steps || [];
      const currentStep = vr.order_item?.current_step;

      productSteps.forEach((step) => {
        steps.add(step);
        const currentIdx =
          currentStep === REGISTRATION_STEPS.DONE
            ? productSteps.length
            : productSteps.indexOf(currentStep);
        const stepIdx = productSteps.indexOf(step);

        let hasCompletedThisStep = false;
        if (step === REGISTRATION_STEPS.QUESTIONAIRE) {
          hasCompletedThisStep = hasCompletedQuestionnaire;
        } else if (step === REGISTRATION_STEPS.MEDNET_REG) {
          hasCompletedThisStep = vr.registered;
        } else if (stepIdx >= 0 && currentIdx >= 0) {
          hasCompletedThisStep = currentIdx > stepIdx;
        }

        if (completion.has(step)) {
          completion.set(step, completion.get(step)! && hasCompletedThisStep);
        } else {
          completion.set(step, hasCompletedThisStep);
        }
      });
    });

    return { requiredSteps: steps, stepCompletion: completion };
  }, [user.visit_registrations, hasCompletedQuestionnaire]);

  const steps = useMemo<JourneyStep[]>(() => {
    const allSteps: JourneyStep[] = [
      {
        id: "email-verification",
        label: "El. pašto patvirtinimas",
        status: isEmailVerified ? "completed" : "pending",
        requiredStep: "always",
        isCompleted: isEmailVerified,
      },
      {
        id: "questionnaire",
        label: "Klausimynas",
        status: "pending",
        requiredStep: REGISTRATION_STEPS.QUESTIONAIRE,
        isCompleted:
          stepCompletion.get(REGISTRATION_STEPS.QUESTIONAIRE) === true,
      },
      {
        id: "doctor-consultation",
        label: "Gydytojo konsultacija",
        status: "pending",
        requiredStep: REGISTRATION_STEPS.CALENDLY_REG,
        isCompleted:
          stepCompletion.get(REGISTRATION_STEPS.CALENDLY_REG) === true,
      },
      {
        id: "sample-submit-registration",
        label: "Registracija mėginių pridavimui",
        status: "pending",
        requiredStep: REGISTRATION_STEPS.MEDNET_REG,
        isCompleted: stepCompletion.get(REGISTRATION_STEPS.MEDNET_REG) === true,
      },
      {
        id: "vo2max-calendly",
        label: "VO₂ max",
        status: "pending",
        requiredStep: REGISTRATION_STEPS.VO2MAX_CALENDLY_REG,
        isCompleted:
          stepCompletion.get(REGISTRATION_STEPS.VO2MAX_CALENDLY_REG) === true,
      },
    ];

    // Filter steps based on required steps
    const filteredSteps = allSteps.filter(
      (step) =>
        step.requiredStep === "always" ||
        (step.requiredStep && requiredSteps.has(step.requiredStep)),
    );

    // Find the first incomplete step and mark it as action-required
    let foundNextStep = false;
    const stepsWithStatus = filteredSteps.map((step) => {
      if (step.isCompleted) {
        return { ...step, status: "completed" as StepStatus };
      }

      if (!foundNextStep) {
        foundNextStep = true;

        // Set up action for the next incomplete step
        if (step.id === "email-verification") {
          return {
            ...step,
            status: "action-required" as StepStatus,
            action: {
              label: "Patvirtinti",
              onClick: handleResendEmail,
              loading: resendingEmail,
              successMsg: emailSent ? "Laiškas išsiųstas" : undefined,
            },
          };
        }
        return {
          ...step,
          status: "action-required" as StepStatus,
          action: {
            label: step.id === "questionnaire" ? "Pradėti" : "Registruotis",
            onClick: () => navigate(ROUTES.REGISTER),
            loading: false,
          },
        };
      }

      // All steps after the next one remain pending with no interaction
      return { ...step, status: "pending" as StepStatus };
    });

    return stepsWithStatus;
  }, [
    isEmailVerified,
    stepCompletion,
    resendingEmail,
    emailSent,
    navigate,
    requiredSteps,
    handleResendEmail,
  ]);

  return (
    <div className="flex flex-col gap-5">
      {steps.map((step) => (
        <div
          key={step.id}
          className={cn(
            "flex flex-col border items-start justify-between gap-4 rounded-xl text-ink px-7.5 py-5 md:flex-row md:items-center",
          )}
        >
          <div className="flex flex-1 items-center gap-5">
            {getIcon(step.status)}

            <span className=" text-[1.375rem] leading-7 font-medium">
              {step.label}
            </span>
          </div>

          {step.action && (
            <div className="flex items-center gap-3">
              {!step.action.successMsg && (
                <Button
                  onClick={step.action.onClick}
                  disabled={step.action.loading}
                  className="h-[56px] w-[136px] rounded-full px-6 py-2 text-lg font-medium whitespace-nowrap"
                  variant="ink"
                >
                  {step.action.loading ? "Vykdoma…" : step.action.label}
                </Button>
              )}
              {step.action.successMsg && (
                <span className="text-green text-sm whitespace-nowrap">
                  {step.action.successMsg}
                </span>
              )}
            </div>
          )}

         
        </div>
      ))}
    </div>
  );
};

export const UserJourneyPanel = () => {
  const { user } = useAuth();

  if (!user) return null;

  return (
    <div className="mb-20">
      <div className="mb-11 flex flex-col items-start justify-between gap-4 md:flex-row">
        <h2 className="text-ink text-[3.5rem] leading-[3.625rem] font-semibold">
          Jūsų žingsniai
        </h2>

        <p className="text-grey-400 max-w-112 text-base leading-[150%]">
          Sekite savo pažangą – čia matysite visus etapus nuo registracijos iki
          konsultacijų.
        </p>
      </div>

      <div className="bg-primary text-ink max-w-150 border rounded-[0.875rem] px-7.5 py-12.5">
        <p className="mb-6 text-xl leading-7 text-ink">
          Siekiantiems maksimalaus ilgaamžiškumo, rezultatų ir ligų prevencijos.
        </p>

        <UserJourneyPanelSteps user={user} />
      </div>
    </div>
  );
};
